import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Camera, ChevronLeft, ChevronRight } from 'lucide-react';
// --- ANIMATION VARIANTS ---
export const staggerContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export const fadeUpItem = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export const scaleUpItem = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  show: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
// --- COMPONENTS ---
export function SlideWrapper({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    filter: 'blur(10px)'
  }} animate={{
    opacity: 1,
    filter: 'blur(0px)'
  }} exit={{
    opacity: 0,
    filter: 'blur(10px)'
  }} transition={{
    duration: 0.6,
    ease: [0.16, 1, 0.3, 1]
  }} className={`absolute top-0 left-0 right-0 bottom-0 w-full flex-1 overflow-y-auto md:overflow-y-hidden overflow-x-hidden flex flex-col px-6 md:px-24 lg:px-32 py-24 md:py-12 touch-pan-y ${className}`} data-id="element-9">
      <div className="flex-1 flex flex-col w-full max-w-7xl mx-auto min-h-fit items-center justify-center lg:min-h-0" data-id="element-10">
        {children}
      </div>
    </motion.div>;
}
export function ScreenshotPlaceholder({
  label = 'Add Screenshot Here',
  className = ''
}: {
  label?: string;
  className?: string;
}) {
  return <div className={`flex flex-col items-center justify-center w-full min-h-[15rem] border-2 border-dashed border-vexu-500/30 rounded-2xl bg-white text-vexu-600/70 backdrop-blur-sm transition-all hover:bg-mint-50 hover:border-vexu-500/50 shadow-sm ${className}`} data-id="element-11">
      <Camera className="w-10 h-10 mb-3 opacity-70" data-id="element-12" />
      <span className="text-sm font-medium tracking-widest uppercase" data-id="element-13">
        {label}
      </span>
    </div>;
}
interface NavProps {
  current: number;
  total: number;
  onNext: () => void;
  onPrev: () => void;
  onGoTo: (index: number) => void;
}
export function SlideNavigation({
  current,
  total,
  onNext,
  onPrev,
  onGoTo
}: NavProps) {
  return <>
      {/* Side Arrows */}
      <button onClick={onPrev} disabled={current === 0} className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 p-4 rounded-full bg-black/0 hover:bg-black/5 text-gray-400 hover:text-charcoal-900 transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none z-50 group" data-id="element-14">
        <ChevronLeft className="w-8 h-8 group-hover:-translate-x-1 transition-transform" data-id="element-15" />
      </button>
      <button onClick={onNext} disabled={current === total - 1} className="hidden md:block absolute right-6 top-1/2 -translate-y-1/2 p-4 rounded-full bg-black/0 hover:bg-black/5 text-gray-400 hover:text-charcoal-900 transition-all duration-300 disabled:opacity-0 disabled:pointer-events-none z-50 group" data-id="element-16">
        <ChevronRight className="w-8 h-8 group-hover:translate-x-1 transition-transform" data-id="element-17" />
      </button>

      {/* Bottom Bar */}
      <div className=" bottom-0 left-0 right-0 flex flex-col items-center gap-3 py-4 z-50" data-id="element-18">
        <div className="flex gap-2.5" data-id="element-19">
          {Array.from({
          length: total
        }).map((_, i) => <button key={i} onClick={() => onGoTo(i)} className={`h-1.5 rounded-full transition-all duration-500 ease-out ${i === current ? 'w-10 bg-vexu-500 shadow-[0_0_10px_rgba(0,200,83,0.3)]' : 'w-2.5 bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-20" />)}
        </div>
        <div className="text-xs font-semibold tracking-[0.2em] text-gray-400 font-heading" data-id="element-21">
          {String(current + 1).padStart(2, '0')} / {total}
        </div>
      </div>
    </>;
}